/**
 * Inventory Products API Client
 *
 * Wraps the /api/v1/inventory/products/master and /api/v1/inventory/products
 * (catalog) and /api/v1/inventory/lookups endpoints from tms-api-inventoryManagement.
 * Requests are routed through the API gateway (prefix: inventoryManagement)
 * using the shared client.
 *
 * Endpoints covered:
 *   GET    /api/v1/inventory/products/master                  — list master (non-tenant) products
 *   GET    /api/v1/inventory/products                         — list hospital catalog
 *   GET    /api/v1/inventory/products/:product_id             — get catalog product
 *   POST   /api/v1/inventory/products/catalog                 — add to hospital catalog
 *   PATCH  /api/v1/inventory/products/:product_id/catalog     — update catalog product
 *   DELETE /api/v1/inventory/products/:product_id/catalog     — remove from catalog
 *   GET    /api/v1/inventory/lookups                          — get lookup values
 *
 * All responses follow the standard envelope:
 *   { success: true, data: T, meta: { requestId, timestamp, ... } }
 *
 * The helpers here return the unwrapped `data` payload for convenience.
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type { MasterProductListItem } from '@tms-phoenix/tms-types';
import type {
  ApiCatalogProduct,
  ApiLookupItem,
  AddToCatalogPayload,
  UpdateCatalogProductPayload,
  LookupType,
  MasterProductListQuery,
} from '../types/inventory-management-api.types';

// ── API client ────────────────────────────────────────────────────────────────

const API_PREFIX = API_ROUTES.INVENTORY_MANAGEMENT;
const apiClient = getApiClient();

/** Base path for all catalog product routes */
const BASE = `${API_PREFIX}/inventory/products`;
const BASE_MASTER = `${API_PREFIX}/inventory/products/master`;
const LOOKUPS_BASE = `${API_PREFIX}/inventory/lookups`;

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * GET /api/v1/inventory/products/master
 *
 * Returns master (non-tenant) products. No `hospital_id` required — master
 * records are global and not tenant-scoped. Accepts optional `search`
 * (substring match on product_name / brand_name) and paginates results by
 * default using `page` / `limit`.
 *
 * Note: createApiClient interceptor auto-unwraps { success, data, meta } →
 * res.data is already MasterProductListItem[].
 */
export async function listMasterProducts(
  params?: Partial<MasterProductListQuery>,
  signal?: AbortSignal,
): Promise<MasterProductListItem[]> {
  const res = await apiClient.get(BASE_MASTER, { params, signal });
  return res.data as MasterProductListItem[];
}

/**
 * GET /api/v1/inventory/products/master/:product_id
 *
 * Returns a single master product by id.
 */
export async function getMasterProduct(
  productId: string,
  signal?: AbortSignal,
): Promise<MasterProductListItem> {
  const encodedProductId = encodePathSegment(productId);
  const res = await apiClient.get(`${BASE_MASTER}/${encodedProductId}`, { signal });
  return res.data as MasterProductListItem;
}

/**
 * GET /api/v1/inventory/products/:product_id
 *
 * Returns a hospital's catalog copy of a specific product, including
 * hospital-specific overrides and full nutrient / allergen data.
 * Returns the unwrapped `data` object.
 *
 * Throws 404 (ApiError) when the product is not in this hospital's catalog.
 */
export async function getCatalogProduct(
  productId: string,
): Promise<ApiCatalogProduct> {
  // createApiClient interceptor auto-unwraps { success, data } → res.data is already ApiCatalogProduct
  const encodedProductId = encodePathSegment(productId);
  const res = await apiClient.get(`${BASE}/${encodedProductId}`);
  return res.data as ApiCatalogProduct;
}

/**
 * POST /api/v1/inventory/products/catalog
 *
 * Adds a product to a hospital's formulary catalog.
 *
 * Case 1 (existing): `productId` supplied → master data used as base.
 * Case 2 (new):      `productId` omitted  → `sku` & `product_name` required
 *                    in the payload; a master record is created first.
 *
 * Returns the created catalog copy (201).
 *
 * Throws:
 *   400 — validation error (e.g. missing hospital_id)
 *   404 — master product not found (Case 1)
 *   409 — product already in this hospital's catalog
 */
export async function addProductToCatalog(
  productId: string | undefined,
  payload: AddToCatalogPayload,
): Promise<ApiCatalogProduct> {
  // Backend POST route is /catalog (product_id goes in the body, not the URL path).
  // createApiClient interceptor auto-unwraps { success, data } → res.data is already ApiCatalogProduct
  const body = { ...payload };
  if (productId) {
    body.product_id = productId;
  }
  const res = await apiClient.post(`${BASE}/catalog`, body);
  return res.data as ApiCatalogProduct;
}

/**
 * PATCH /api/v1/inventory/products/:product_id/catalog
 *
 * Partially updates hospital-specific overrides on a catalog product.
 * Returns the updated catalog product.
 *
 * Throws 404 when the product is not found in this hospital's catalog.
 */
export async function updateCatalogProduct(
  productId: string,
  payload: UpdateCatalogProductPayload,
): Promise<ApiCatalogProduct> {
  // createApiClient interceptor auto-unwraps { success, data } → res.data is already ApiCatalogProduct
  const encodedProductId = encodePathSegment(productId);
  const res = await apiClient.patch(`${BASE}/${encodedProductId}/catalog`, payload);
  return res.data as ApiCatalogProduct;
}

/**
 * DELETE /api/v1/inventory/product/:product_id/permanent
 *
 * Permanently removes a product from a hospital's formulary catalog (hard
 * delete of the catalog copy only — the master product is never affected).
 *
 * Throws:
 *   404 — product not found in this hospital's catalog
 *   409 — available inventory lots exist for this product/hospital
 */
export async function removeFromCatalog(
  productId: string,
): Promise<void> {
  const encodedProductId = encodePathSegment(productId);
  await apiClient.delete(`${API_PREFIX}/inventory/product/${encodedProductId}/permanent`);
}

/**
 * GET /api/v1/inventory/lookups
 *
 * Returns all distinct attribute suggestion values for the given lookup type.
 * Useful for populating dropdowns (manufacturer, brand, category, form).
 *
 * @param type  — 'manufacturer' | 'brand' | 'category' | 'form'
 * @param manufacturerName — only used when type='brand'; filters brands by manufacturer
 */
export async function getInventoryLookups(
  type: LookupType,
  manufacturerName?: string,
): Promise<ApiLookupItem[]> {
  const params: Record<string, string> = { type };
  if (type === 'brand' && manufacturerName) {
    params['manufacturer_name'] = manufacturerName;
  }
  // createApiClient interceptor auto-unwraps { success, data } → res.data is already ApiLookupItem[]
  const res = await apiClient.get(LOOKUPS_BASE, { params });
  return res.data as ApiLookupItem[];
}
